import { Users, HardHat } from 'lucide-react';
import { DAY_NAMES_SHORT } from './utils';

/**
 * Toolbar chip strip showing per-firm assignment summary on the planning week
 * board (e.g. "AYTU 3 · FAST-TRANS 1 · HYK 4"). Each chip is clickable —
 * clicking it opens a popover listing the firm's assigned employees with a
 * mini week strip (Pzt..Cmt) per row so the operator can spot who's missing
 * a day.
 *
 * Props:
 *   counts        : array of { id, name, count, employees: [{id, name, count, days: Set<date>}] }
 *   openFirmId    : currently expanded firm id (controlled)
 *   setOpenFirmId : setter
 *   dates         : 7-element array of YYYY-MM-DD for the visible week (Mon..Sun)
 *   isDateLocked  : (date) => boolean
 *   lockedDates   : { [date]: reason }
 */
export function FirmCountChips({ counts, openFirmId, setOpenFirmId, dates, isDateLocked, lockedDates }) {
  if (!counts || counts.length === 0) return null;
  return (
    <div className="flex items-center gap-1.5 pr-2 mr-1 border-r border-slate-200 dark:border-slate-700" data-testid="planning-firm-counts">
      {counts.map((f) => {
        const isOpen = openFirmId === f.id;
        const isSubc = !!f.isSubcontractor;
        return (
          <div key={f.id} className="relative">
            <button
              type="button"
              onClick={() => setOpenFirmId(isOpen ? null : f.id)}
              className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-semibold border transition-colors ${
                isSubc
                  ? f.count > 0
                    ? 'bg-amber-50 dark:bg-amber-900/30 border-amber-300 dark:border-amber-700 text-amber-800 dark:text-amber-300 hover:bg-amber-100 dark:hover:bg-amber-900/50'
                    : 'bg-slate-50 dark:bg-slate-800 border-slate-200 dark:border-slate-700 text-slate-400'
                  : f.count > 0
                  ? 'bg-indigo-50 dark:bg-indigo-900/30 border-indigo-200 dark:border-indigo-700 text-indigo-700 dark:text-indigo-300 hover:bg-indigo-100 dark:hover:bg-indigo-900/50'
                  : 'bg-slate-50 dark:bg-slate-800 border-slate-200 dark:border-slate-700 text-slate-400 hover:bg-slate-100 dark:hover:bg-slate-700/50'
              } ${isOpen ? (isSubc ? 'ring-2 ring-amber-400/60' : 'ring-2 ring-indigo-400/60') : ''}`}
              title={isSubc
                ? `Taşeron: ${f.count} farklı sürücü bu hafta görev aldı (manuel kayıtlar)`
                : `${f.name}: ${f.count} farklı çalışan bu hafta görev aldı (Pazar hariç) — detay için tıkla`}
              data-testid={`planning-firm-count-${f.id}`}
            >
              {isSubc ? <HardHat className="h-2.5 w-2.5" /> : <Users className="h-2.5 w-2.5" />}
              {f.name}
              <span className="tabular-nums">{f.count}</span>
            </button>
            {isOpen && (
              <FirmEmployeesPopover
                firm={f}
                dates={dates}
                isDateLocked={isDateLocked}
                lockedDates={lockedDates}
                onClose={() => setOpenFirmId(null)}
              />
            )}
          </div>
        );
      })}
    </div>
  );
}

function FirmEmployeesPopover({ firm, dates, isDateLocked, lockedDates, onClose }) {
  return (
    <>
      <div className="fixed inset-0 z-30" onClick={onClose} />
      <div
        className="absolute right-0 top-full mt-1.5 z-40 w-64 max-h-[60vh] overflow-y-auto bg-white dark:bg-slate-800 border border-slate-200 dark:border-slate-700 rounded-lg shadow-xl p-2"
        data-testid={`planning-firm-popover-${firm.id}`}
      >
        <div className="flex items-center justify-between px-1 pb-1.5 border-b border-slate-100 dark:border-slate-700 mb-1.5">
          <span className="text-[11px] font-bold text-slate-700 dark:text-slate-200">
            {firm.name} — {firm.count} kişi
          </span>
          <span className="text-[9px] uppercase tracking-wide text-slate-400">Pzt-Cmt</span>
        </div>
        {firm.employees.length === 0 ? (
          <div className="px-2 py-3 text-[11px] text-slate-400 italic text-center">
            Bu firmadan henüz atanmış çalışan yok
          </div>
        ) : (
          <ul className="space-y-1">
            {firm.employees.map((emp) => (
              <EmployeeMiniWeek
                key={emp.id}
                emp={emp}
                firmId={firm.id}
                dates={dates}
                isDateLocked={isDateLocked}
                lockedDates={lockedDates}
              />
            ))}
          </ul>
        )}
      </div>
    </>
  );
}

function EmployeeMiniWeek({ emp, firmId, dates, isDateLocked, lockedDates }) {
  // Mini hafta şeridi: Pzt..Cmt (6 gün, Pazar hariç). Atanmış gün indigo,
  // kilitli gün rose, boş gün gri. Operatör hangi günler eksik kalmış tek
  // bakışta görür.
  const weekdays = (dates || []).slice(0, 6);
  return (
    <li
      className="px-2 py-1.5 text-[11px] hover:bg-slate-50 dark:hover:bg-slate-700/40 rounded"
      data-testid={`planning-firm-popover-${firmId}-emp-${emp.id}`}
    >
      <div className="flex items-center justify-between mb-1">
        <span className="text-slate-700 dark:text-slate-200 truncate font-medium">{emp.name}</span>
        <span className="ml-2 shrink-0 inline-flex items-center justify-center min-w-[20px] h-4 px-1 text-[10px] font-bold tabular-nums rounded bg-indigo-100 dark:bg-indigo-900/40 text-indigo-700 dark:text-indigo-300">
          {emp.count}
        </span>
      </div>
      <div className="grid grid-cols-6 gap-0.5">
        {weekdays.map((dt, idx) => {
          const assigned = emp.days.has(dt);
          const locked = isDateLocked(dt);
          const dayLetter = DAY_NAMES_SHORT[idx] || '';
          let cls = 'bg-slate-100 dark:bg-slate-700 text-slate-400';
          if (locked) cls = 'bg-rose-100 dark:bg-rose-900/40 text-rose-600 dark:text-rose-300';
          else if (assigned) cls = 'bg-indigo-500 text-white';
          const tip = locked
            ? `${dayLetter}: kilitli (${lockedDates[dt]})`
            : assigned
            ? `${dayLetter}: atanmış`
            : `${dayLetter}: boş`;
          return (
            <span
              key={dt}
              title={tip}
              className={`text-[9px] font-bold text-center py-0.5 rounded ${cls}`}
            >
              {dayLetter}
            </span>
          );
        })}
      </div>
    </li>
  );
}
